import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ServerService, IDeploymentStatus, IDeploymentLog } from '../service/serverService';
import { AccountService, IAccount } from '../service/accountService';
import { GitBranchService } from '../service/gitBranchService';
import { SSHService } from '../service/sshService';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Table, TableContainer, TableHead, TableBody, TableRow, TableCell, Paper, 
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Collapse, Chip, Divider,
  TextField, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, Tooltip,
  Grid, Card, CardContent, CardActions, Menu, ListItemIcon
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import StorageIcon from '@mui/icons-material/Storage';
import CircleIcon from '@mui/icons-material/Circle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GetAppIcon from '@mui/icons-material/GetApp';
import TerminalIcon from '@mui/icons-material/Terminal';
import { ServerDeploymentDialog } from './ServerDeploymentDialog';
import { useSshHandler } from '../hooks/useSshHandler';

export interface Server {
  ref: string;
  name: string;
  ip: string;
  user: string;
  password: string;
  description: string;
  gitBranch?: string;
  isDeploying?: boolean;
  deploymentStarted?: string;
  // washpark kann eine ObjektId (string) oder ein Account-Objekt sein, wenn populiert
  washpark?: string | IAccount;
  deploymentVersions: Record<string,string>;
}

// Statt einer festen Liste, laden wir die Branches nun vom Server
const DefaultGitBranches = ['main', 'development'];

const ServerList: React.FC = () => {
  const [servers, setServers] = useState<Server[]>([]);
  const [accounts, setAccounts] = useState<IAccount[]>([]);
  const [log, setLog] = useState<string>("");
  const [openDetail, setOpenDetail] = useState<Server | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [selectedGitBranch, setSelectedGitBranch] = useState<string>("");
  const [deploymentLogs, setDeploymentLogs] = useState<IDeploymentLog[]>([]);
  const [loadingDeploymentLogs, setLoadingDeploymentLogs] = useState<boolean>(false);
  const [showDeploymentLogs, setShowDeploymentLogs] = useState<boolean>(false);
  const [showFullHistory, setShowFullHistory] = useState<boolean>(false);
  const [autoScroll, setAutoScroll] = useState<boolean>(true);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [openDeploymentDialog, setOpenDeploymentDialog] = useState<Server | null>(null);
  const [sshPasswordCopiedInDetail, handleOpenSshTerminalInDetail] = useSshHandler();

  // Zustand für die editierbare Branch-Liste
  const [gitBranches, setGitBranches] = useState<string[]>(DefaultGitBranches);
  const [newBranchName, setNewBranchName] = useState<string>("");
  const [verifyingBranch, setVerifyingBranch] = useState<boolean>(false);

  // Serverdaten abrufen
  useEffect(() => {
    async function fetchServers() {
      try {
        const data = await ServerService.getServers();
        console.log("Erhaltene Server-Daten:", data);
        const serverArray = Array.isArray(data) ? data : data.servers;
        setServers(serverArray);
        setLog("Server erfolgreich geladen");
      } catch (error) {
        setLog("Fehler beim Laden der Server");
        console.error("Error in fetchServers:", error);
      }
    }
    fetchServers();
  }, [openDeploymentDialog]);

  // Account-Daten abrufen
  useEffect(() => {
    async function fetchAccounts() {
      try {
        const data = await AccountService.getAccounts();
        console.log("Erhaltene Account-Daten:", data);
        setAccounts(data);
      } catch (error) {
        console.error("Fehler beim Laden der Accounts", error);
      }
    }
    fetchAccounts();
  }, []);

  // Lade Git-Branches vom Server
  useEffect(() => {
    async function fetchGitBranches() {
      try {
        const branches = await GitBranchService.getBranches();
        setGitBranches(branches);
        setLog("Git-Branches erfolgreich geladen");
      } catch (error) {
        setLog("Fehler beim Laden der Git-Branches");
        console.error("Error in fetchGitBranches:", error);
      }
    }
    fetchGitBranches();
  }, []);

  // Function to fetch deployment logs
  const fetchDeploymentLogs = useCallback(async (serverRef: string) => {
    try {
      setLoadingDeploymentLogs(true);
      const deploymentStatus = await ServerService.getDeploymentLogs(serverRef, showFullHistory);
      setDeploymentLogs(deploymentStatus.logs);
      
      // Update server status using functional update to avoid stale closures
      setServers(prevServers => 
        prevServers.map(server => 
          server.ref === serverRef 
            ? { ...server, isDeploying: deploymentStatus.isDeploying } 
            : server
        )
      );
      
      setLoadingDeploymentLogs(false);
    } catch (error) {
      console.error("Fehler beim Abrufen der Deployment-Logs:", error);
      setLoadingDeploymentLogs(false);
    }
  }, [showFullHistory]);

  // Polling effect for deployment status
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    const deployingServer = servers.find(server => server.isDeploying);
    
    if (deployingServer && openDetail && openDetail.ref === deployingServer.ref && showDeploymentLogs) {
      interval = setInterval(() => {
        fetchDeploymentLogs(deployingServer.ref);
      }, 3000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [servers, openDetail, showDeploymentLogs, fetchDeploymentLogs]);

  // Auto-scroll to bottom when logs update
  useEffect(() => {
    if (autoScroll && logContainerRef.current && deploymentLogs.length > 0) {
      const container = logContainerRef.current;
      // Slight delay to ensure rendering is complete
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
      }, 100);
    }
  }, [deploymentLogs, autoScroll]);

  // Handle scroll events to detect if user has scrolled away from bottom
  const handleLogScroll = () => {
    if (!logContainerRef.current) return;
    
    const container = logContainerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // If we're near the bottom (within 50px), keep auto-scroll enabled
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;
    setAutoScroll(isNearBottom);
  };

  // Funktion zum Hinzufügen eines neuen Branches
  const handleAddBranch = async () => {
    if (!newBranchName.trim()) return;
    
    // Prüfen, ob der Branch bereits existiert
    if (!gitBranches.includes(newBranchName)) {
      setVerifyingBranch(true);
      try {
        // Überprüfe, ob der Branch im Repository existiert
        const branchExists = await GitBranchService.verifyBranch(newBranchName);
        
        if (branchExists) {
          // Branch existiert im Repository und wurde bereits zum Service hinzugefügt
          // Lade die aktualisierte Liste direkt, ohne addBranch erneut aufzurufen
          const updatedBranches = await GitBranchService.getBranches();
          setGitBranches(updatedBranches);
          setNewBranchName(""); // Eingabefeld zurücksetzen
          setLog(`Branch '${newBranchName}' wurde hinzugefügt.`);
        } else {
          // Branch existiert nicht im Repository
          setLog(`Warnung: Branch '${newBranchName}' existiert nicht im Repository.`);
          
          // Optional: Frage, ob der Branch trotzdem hinzugefügt werden soll
          const confirmAdd = window.confirm(
            `Der Branch '${newBranchName}' wurde im Repository nicht gefunden. Möchten Sie ihn trotzdem zur Liste hinzufügen?`
          );
          
          if (confirmAdd) {
            const updatedBranches = await GitBranchService.addBranch(newBranchName);
            setGitBranches(updatedBranches);
            setNewBranchName(""); // Eingabefeld zurücksetzen
            setLog(`Branch '${newBranchName}' wurde trotz Warnung hinzugefügt.`);
          }
        }
      } catch (error) {
        console.error("Fehler bei der Branch-Überprüfung:", error);
        setLog(`Fehler bei der Überprüfung des Branches '${newBranchName}'.`);
      } finally {
        setVerifyingBranch(false);
      }
    } else {
      // Branch existiert bereits in der Liste
      setLog(`Branch '${newBranchName}' existiert bereits in der Liste.`);
    }
  };

  // Funktion zum Entfernen eines Branches
  const handleRemoveBranch = async (branchToRemove: string) => {
    try {
      // Entferne den Branch über den Service
      const updatedBranches = await GitBranchService.removeBranch(branchToRemove);
      setGitBranches(updatedBranches);
      
      // Wenn der ausgewählte Branch entfernt wird, setze die Auswahl zurück
      if (selectedGitBranch === branchToRemove) {
        setSelectedGitBranch("");
      }
      
      setLog(`Branch '${branchToRemove}' wurde entfernt.`);
    } catch (error) {
      console.error("Fehler beim Entfernen des Branches:", error);
      setLog(`Fehler beim Entfernen des Branches '${branchToRemove}'.`);
    }
  };

  const handleDeployServer = async (serverRef: string) => {
    try {
      // Sofort den lokalen Status aktualisieren, um mehrfaches Klicken zu verhindern
      setServers(prevServers => 
        prevServers.map(server => 
          server.ref === serverRef 
            ? { ...server, isDeploying: true } 
            : server
        )
      );
      
      // Wenn wir in den Server-Details sind, deaktivieren wir den Button
      if (openDetail && openDetail.ref === serverRef) {
        setOpenDetail(prev => prev ? { ...prev, isDeploying: true } : null);
        setShowDeploymentLogs(true);
      }
      
      // Log-Nachricht zeigen, dass Deployment gestartet wird
      setLog(`Deployment für Server ${serverRef} wird gestartet...`);
      
      // Jetzt erst den Server-Request senden
      await ServerService.deployServer(serverRef);
      
      // Erfolgslog nach der Antwort
      setLog(`Deployment für Server ${serverRef} gestartet`);
      
      // Nach erfolgreicher Antwort aktualisierten Status vom Server holen
      try {
        const updatedServers = await ServerService.getServers();
        setServers(updatedServers);
      } catch (serverUpdateError) {
        console.error("Fehler beim Aktualisieren der Serverliste:", serverUpdateError);
        // Der lokale Status wurde bereits aktualisiert, also kein weiteres Eingreifen nötig
      }
      
      // Falls wir in den Server-Details sind, die Logs anzeigen
      if (openDetail && openDetail.ref === serverRef) {
        fetchDeploymentLogs(serverRef);
      }
    } catch (error) {
      console.error("Fehler beim Deployment:", error);
      setLog(`Fehler beim Deployment von Server ${serverRef}`);
      
      // Bei einem Fehler den Deployment-Status zurücksetzen
      setServers(prevServers => 
        prevServers.map(server => 
          server.ref === serverRef 
            ? { ...server, isDeploying: false } 
            : server
        )
      );
      
      if (openDetail && openDetail.ref === serverRef) {
        setOpenDetail(prev => prev ? { ...prev, isDeploying: false } : null);
      }
    }
  };

  const handleStopDeployment = async (serverRef: string) => {
    try {
      await ServerService.stopDeployment(serverRef);
      setLog(`Deployment für Server ${serverRef} gestoppt`);
      
      // Status aktualisieren
      const updatedServers = await ServerService.getServers();
      setServers(updatedServers);
      
      // Deployment-Logs aktualisieren, wenn sie angezeigt werden
      if (openDetail && openDetail.ref === serverRef && showDeploymentLogs) {
        await fetchDeploymentLogs(serverRef);
      }
    } catch (error) {
      console.error("Fehler beim Stoppen des Deployments:", error);
      setLog(`Fehler beim Stoppen des Deployments von Server ${serverRef}`);
    }
  };

  const toggleDeploymentLogs = async (server: Server) => {
    if (!showDeploymentLogs) {
      await fetchDeploymentLogs(server.ref);
    }
    setShowDeploymentLogs(!showDeploymentLogs);
  };

  const toggleDetails = (server: Server) => {
    setOpenDetail(server);
    setShowDeploymentLogs(false);
    setDeploymentLogs([]);
  };

  const renderWashpark = (washpark?: string | IAccount) => {
    if (!washpark) return "N/A";
    if (typeof washpark === "object") {
      return washpark.displayName || "N/A";
    }
    return washpark;
  };

  const handleAssignAccount = async (serverRef: string) => {
    if (!selectedAccount) {
      console.warn("Kein Account ausgewählt!");
      return;
    }
    try {
      await ServerService.assignWashpark(serverRef, selectedAccount);
      setLog(`Account zu Server ${serverRef} zugewiesen`);
      
      // Serverliste neu laden, um den Account-Namen korrekt zu erhalten
      const updatedServers = await ServerService.getServers();
      setServers(updatedServers);
    } catch (error) {
      console.error("Fehler beim Zuweisen des Accounts", error);
      setLog("Fehler beim Zuweisen des Accounts");
    }
  };

  const handleUpdateGitBranch = async (serverRef: string) => {
    if (!selectedGitBranch) {
      console.warn("Kein Git-Branch ausgewählt!");
      return;
    }
    try {
      await ServerService.updateGitBranch(serverRef, selectedGitBranch);
      setLog(`Git-Branch für Server ${serverRef} aktualisiert auf ${selectedGitBranch}`);
      
      // Serverliste neu laden, um den Branch-Namen korrekt zu erhalten
      const updatedServers = await ServerService.getServers();
      setServers(updatedServers);
      setOpenDetail(null);
    } catch (error) {
      console.error("Fehler beim Aktualisieren des Git-Branches", error);
      setLog("Fehler beim Aktualisieren des Git-Branches");
    }
  };

  const getLogIcon = (type: string) => {
    switch(type) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getDeploymentStatus = (server: Server) => {
    if (server.isDeploying) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2" color="primary">Deployment läuft</Typography>
        </Box>
      );
    }
    return <Typography sx={{ color: 'green', fontWeight: 'bold' }}>✔</Typography>;
  };

  // Toggle zwischen aktuellen und allen Logs
  const toggleFullHistory = async () => {
    setShowFullHistory(!showFullHistory);
    // Logs neu laden, wenn Deployment-Logs angezeigt werden
    if (showDeploymentLogs && openDetail) {
      await fetchDeploymentLogs(openDetail.ref);
    }
  };

  const handleDownloadYaml = async (serverRef: string) => {
    try {
      await ServerService.downloadDeploymentYaml(serverRef);
      setLog(`YAML-Konfiguration für Server ${serverRef} wurde heruntergeladen`);
    } catch (error) {
      console.error("Fehler beim Herunterladen der YAML-Konfiguration:", error);
      setLog(`Fehler beim Herunterladen der YAML-Konfiguration für Server ${serverRef}`);
    }
  };

  const renderDeploymentLogOptions = () => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            onClick={() => toggleDeploymentLogs(openDetail!)}
          >
            {showDeploymentLogs ? "Deployment-Logs ausblenden" : "Deployment-Logs anzeigen"}
          </Button>
          
          {showDeploymentLogs && (
            <Button
              variant="outlined"
              color={showFullHistory ? "primary" : "inherit"}
              onClick={toggleFullHistory}
              size="small"
            >
              {showFullHistory ? "Nur aktuelles Deployment" : "Vollständige Historie"}
            </Button>
          )}
          
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<GetAppIcon />}
            onClick={() => handleDownloadYaml(openDetail!.ref)}
            sx={{ ml: 1 }}
          >
            YAML herunterladen
          </Button>
        </Box>
        
        <Box>
          {showDeploymentLogs && (
            <Button 
              size="small"
              variant="text" 
              onClick={() => {
                setAutoScroll(true);
                if (logContainerRef.current) {
                  logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
                }
              }}
              startIcon={<InfoIcon />}
            >
              {autoScroll ? "Auto-Scroll aktiv" : "Zum Ende scrollen"}
            </Button>
          )}
          
          {/* Stopp-Button für laufende Deployments */}
          {openDetail?.isDeploying && (
            <Button 
              variant="contained"
              color="error"
              size="small"
              sx={{ ml: 1 }}
              onClick={() => handleStopDeployment(openDetail.ref)}
            >
              Deployment abbrechen
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  const renderDialogContent = () => {
    if (!openDetail) return null;
    
    return (
      <Box>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mt: 2,
          mb: 3,
          pb: 2,
          borderBottom: '1px solid #eaeaea' 
        }}>
          <StorageIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'medium', mr: 2 }}>
            {openDetail.name}
          </Typography>
          <Chip 
            icon={<CircleIcon sx={{ fontSize: '0.8rem !important' }} />}
            label={openDetail.isDeploying ? "Deployment läuft" : "Online"}
            size="small"
            sx={{ 
              backgroundColor: openDetail.isDeploying ? '#FF950020' : '#30D15820',
              color: openDetail.isDeploying ? '#FF9500' : '#30D158',
              fontWeight: 'bold',
              '& .MuiChip-icon': {
                color: openDetail.isDeploying ? '#FF9500' : '#30D158',
                marginLeft: '8px',
              }
            }}
          />
          
          <Box sx={{ ml: 'auto' }}>
            <Button 
              variant="contained" 
              startIcon={<TerminalIcon />}
              onClick={() => openDetail && handleOpenSshTerminalInDetail(openDetail, 'ServerListDetails')}
              sx={{ backgroundColor: '#2e7d32', color: 'white' }}
            >
              {sshPasswordCopiedInDetail ? "Passwort kopiert!" : "SSH Terminal öffnen"}
            </Button>
          </Box>
        </Box>

        {/* Server Information Card */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            border: '1px solid #eaeaea',
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', mb: 2 }}>
            Server Information
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Server ID"
                value={openDetail.ref}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton onClick={() => navigator.clipboard.writeText(openDetail.ref)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
                variant="outlined"
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="IP Adresse"
                value={openDetail.ip}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton onClick={() => navigator.clipboard.writeText(openDetail.ip)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
                variant="outlined"
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Benutzer"
                value={openDetail.user}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton onClick={() => navigator.clipboard.writeText(openDetail.user)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
                variant="outlined"
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Passwort"
                value={openDetail.password}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton onClick={() => navigator.clipboard.writeText(openDetail.password)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
                variant="outlined"
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Beschreibung"
                value={openDetail.description}
                multiline
                rows={2}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                backgroundColor: openDetail?.washpark ? 'rgba(46, 125, 50, 0.1)' : 'rgba(0, 0, 0, 0.05)', 
                p: 2, 
                borderRadius: 1,
                mb: 1 
              }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Waschpark-Zuweisung
                  </Typography>
                  {openDetail?.washpark && typeof openDetail.washpark === "object" ? (
                    <Typography variant="body2">
                      Diesem Server ist der Waschpark <strong>{openDetail.washpark.displayName}</strong> zugewiesen
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      Diesem Server ist kein Waschpark zugewiesen
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        {/* Deployment-Logs-Bereich */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            border: '1px solid #eaeaea',
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', mb: 2 }}>
            Deployment Status & Actions
          </Typography>
          
          {renderDeploymentLogOptions()}
            
          <Collapse in={showDeploymentLogs}>
            <Paper 
              variant="outlined" 
              sx={{ 
                mt: 2, 
                p: 2, 
                maxHeight: '300px', 
                overflowY: 'auto',
                bgcolor: '#f8f9fa',
                borderRadius: 1,
                scrollBehavior: 'smooth'
              }}
              ref={logContainerRef}
              onScroll={handleLogScroll}
            >
              {loadingDeploymentLogs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                  <CircularProgress />
                </Box>
              ) : deploymentLogs.length === 0 ? (
                <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>Keine Logs verfügbar.</Typography>
              ) : (
                <List dense>
                  {deploymentLogs.map((log, index) => (
                    <ListItem 
                      key={index} 
                      sx={{ 
                        alignItems: 'flex-start',
                        backgroundColor: log.type === 'error' ? 'rgba(255, 0, 0, 0.08)' : 'transparent',
                      }}
                    >
                      <Box sx={{ mr: 1.5, mt: 0.5 }}>{getLogIcon(log.type)}</Box>
                      <ListItemText 
                        primary={log.message}
                        secondary={new Date(log.timestamp).toLocaleString()}
                        primaryTypographyProps={{ 
                          style: { 
                            color: log.type === 'error' ? 'red' : 'inherit',
                            whiteSpace: 'pre-wrap'
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Collapse>
        </Paper>
        
        {/* Account-Zuweisung-Bereich */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            border: '1px solid #eaeaea',
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', mb: 2 }}>
            Waschpark zuweisen
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="account-select-label">Waschpark auswählen</InputLabel>
            <Select
              labelId="account-select-label"
              value={selectedAccount}
              label="Waschpark auswählen"
              onChange={(e) => {
                console.log("Selected Account:", e.target.value);
                setSelectedAccount(e.target.value);
              }}
            >
              {accounts.map((account) => (
                <MenuItem key={account._id} value={account._id}>
                  {account.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            sx={{ 
              backgroundColor: 'black', 
              color: 'white',
              '&:hover': {
                backgroundColor: '#333',
              }
            }}
            onClick={() => handleAssignAccount(openDetail.ref)}
            disabled={!selectedAccount}
            fullWidth
          >
            Waschpark zuweisen
          </Button>
        </Paper>
        
        {/* Git-Branch-Verwaltung */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            border: '1px solid #eaeaea',
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', mb: 2 }}>
            Git Branch Konfiguration
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel id="git-branch-select-label">Aktueller Git-Branch</InputLabel>
            <Select
              labelId="git-branch-select-label"
              value={selectedGitBranch || openDetail.gitBranch || 'main'}
              label="Aktueller Git-Branch"
              onChange={(e) => setSelectedGitBranch(e.target.value as string)}
            >
              {gitBranches.map((branch) => (
                <MenuItem key={branch} value={branch}>
                  {branch}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="contained"
            color="primary"
            onClick={() => handleUpdateGitBranch(openDetail!.ref)}
            disabled={!selectedGitBranch || selectedGitBranch === (openDetail.gitBranch || 'main')}
            sx={{ mt: 1, backgroundColor: 'black', color: 'white' }}
          >
            Branch für Server aktualisieren
          </Button>
        </Paper>
      </Box>
    );
  };

  // In the ServerList component, add a function to handle SSH for a specific server
  const handleSSHForServer = (server: Server, event: React.MouseEvent<HTMLElement>) => {
    SSHService.openSSHTerminal(server.ip, server.user, server.password);
    event.stopPropagation(); // Prevent card click (which would open details)
  };

  return (
    <Box>
      <Grid container spacing={4}>
        {servers.map((server) => (
          <Grid item xs={12} sm={6} md={4} key={server.ref} sx={{ mb: 2 }}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                },
                mb: 4
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <StorageIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6" component="div" sx={{ fontSize: '1rem' }}>
                      {server.name}
                    </Typography>
                  </Box>
                  <Chip 
                    icon={<CircleIcon sx={{ fontSize: '0.8rem !important' }} />}
                    label={server.isDeploying ? "Deployment läuft" : "Online"}
                    size="small"
                    sx={{ 
                      backgroundColor: server.isDeploying ? '#FF950020' : '#30D15820',
                      color: server.isDeploying ? '#FF9500' : '#30D158',
                      fontWeight: 'bold',
                      '& .MuiChip-icon': {
                        color: server.isDeploying ? '#FF9500' : '#30D158',
                        marginLeft: '8px',
                      }
                    }}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>IP:</strong> {server.ip}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Subdomain:</strong> {server.washpark && typeof server.washpark === "object" ? server.washpark.subdomain : "Keine Subdomain"}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Waschpark:</strong> {renderWashpark(server.washpark)}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  <strong>Git-Branch:</strong> {server.gitBranch || "main"}
                </Typography>
              </CardContent>

              <CardActions sx={{ 
                pt: 0, 
                px: 2, 
                pb: 2,
                mt: 'auto',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1
              }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{ backgroundColor: 'black', color: 'white' }}
                  onClick={() => toggleDetails(server)}
                >
                  Details
                </Button>

                {server.washpark && typeof server.washpark === "object" && (
                  <>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      sx={{ 
                        backgroundColor: server.isDeploying ? 'grey.500' : 'black', 
                        color: 'white' 
                      }}
                      onClick={() => handleDeployServer(server.ref)}
                      disabled={server.isDeploying}
                    >
                      {server.isDeploying ? "Deployment läuft..." : "Deploy"}
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      sx={{ backgroundColor: 'purple', color: 'white' }}
                      onClick={() => setOpenDeploymentDialog(server)}>
                      Deploy (Alpha)
                    </Button>
                    
                    {server.isDeploying && (
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleStopDeployment(server.ref)}
                      >
                        Stopp
                      </Button>
                    )}
                  </>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="body1" sx={{ mt: 2 }}>
        {log}
      </Typography>

      {/* Dialog für Server Details */}
      <Dialog 
        open={Boolean(openDetail)} 
        onClose={() => setOpenDetail(null)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          px: 3, 
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #eaeaea'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <StorageIcon sx={{ color: 'primary.main', mr: 1.5 }} />
            <Typography variant="h6" component="div" sx={{ fontWeight: 'medium' }}>
              Server Details
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setOpenDetail(null)}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {renderDialogContent()}
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          py: 2,
          borderTop: '1px solid #eaeaea'
        }}>
          <Button variant="outlined" onClick={() => setOpenDetail(null)}>
            Schließen
          </Button>
          {openDetail && openDetail.washpark && typeof openDetail.washpark === "object" && (
            <>
              <Button 
                variant="contained"
                sx={{ 
                  backgroundColor: openDetail.isDeploying ? 'grey.500' : 'black', 
                  color: 'white',
                  '&:hover': {
                    backgroundColor: openDetail.isDeploying ? 'grey.600' : '#333',
                  }
                }}
                onClick={() => handleDeployServer(openDetail.ref)}
                disabled={openDetail.isDeploying}
              >
                {openDetail.isDeploying ? "Deployment läuft..." : "Server Deployment starten"}
              </Button>
              
              {openDetail.isDeploying && (
                <Button 
                  variant="contained"
                  color="error"
                  onClick={() => handleStopDeployment(openDetail.ref)}
                >
                  Deployment abbrechen
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
      <ServerDeploymentDialog 
        onClose={() => setOpenDeploymentDialog(null)}
        server={openDeploymentDialog} 
      />
    </Box>
  );
};

export default ServerList;
